// Courage Chat: ask about records and figures, or ask for an update. Claude answers as the signed-in user;
// any change comes back as a proposal with a Confirm button, and only then is it made.
import { Fragment, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api, ApiError, type ChatAction } from "./api";

type Turn = { role: "user" | "assistant"; content: string; actions?: (ChatAction & { status?: "done" | "dismissed" | "failed"; error?: string })[] };

const PROMPTS: Record<string, string[]> = {
  commercial: ["Which deals close this month, and what's the next step on each?", "Which customers have overdue balances with open quotes?", "What happened to gross margin this year, and why?", "Which customers stopped buying an item they ordered regularly?"],
  marketing: ["What's going out this week, and what's late?", "How did our last email do, and who clicked?", "Draft a newsletter from this month's wins", "Which new leads should I call first?"],
  quality: ["What are my open issues?", "Which recurring issues don't have a CAPA yet?", "Summarize this week's issues for the Tier 2 huddle"],
  engineering: ["Which new item requests are waiting on me?", "What's in review for the 2000-ASSY BOM?", "Which new products are behind their gate?"],
  supply: ["Which items are below safety stock?", "Which PO lines are past due, and from whom?", "Which suppliers haven't acknowledged their POs?"],
  production: ["What's down right now, and why?", "What caused the most downtime this month?", "What did the tier boards escalate this week?"],
  inventory: ["Which lots are on hold, and why?", "What did we ship most this quarter?", "Which counts have variances to review?"],
  maintenance: ["What work orders are overdue?", "Which PMs are due this week?", "Which machine had the most downtime this quarter?"],
  safety: ["How many days since our last recordable?", "Which safety actions are still open?", "Summarize this month's near misses"],
  documents: ["Which documents are in review?", "Who hasn't read the latest MRB procedure?", "What changed in our procedures this quarter?"],
  training: ["Whose qualifications expire this month?", "Who is qualified on the press brake?", "Where are we short of trained people?"],
  improve: ["Which processes cost us the most a year?", "What's on each department's roadmap next quarter?", "What did improvement save this year?"],
  requests: ["What's waiting for my approval?", "Which deviations expire soon?", "What did we approve this month?"],
  it: ["What tickets are open, and which are late?", "What breaks most often?", "Which tickets are waiting on the requester?"],
  finance: ["How is revenue tracking against plan?", "What happened to gross margin this quarter?", "Which customers bought less than last year?"],
};
const DEFAULT = ["What's open that I own?", "Find a record by number or name"];

/** Turn record numbers into links and keep line breaks, simple bullets and **bold**. */
const hrefFor = (n: string) => (n.startsWith("OPP-") ? `/opportunities/${n}` : n.startsWith("Q-") ? `/quotes/${n}` : n.startsWith("ACC-") ? `/accounts/${n}` : n.startsWith("WO-") ? `/maintenance/wo/${n}` : n.startsWith("AST-") ? `/maintenance/assets/${n}` : n.startsWith("PM-") ? `/maintenance/pm/${n}` : n.startsWith("LEAD-") ? `/marketing/leads/${n}` : n.startsWith("CMP-") ? `/marketing/campaigns/${n}` : n.startsWith("CNT-") ? `/marketing/calendar` : n.startsWith("EML-") ? `/marketing/emails/${n}` : n.startsWith("SEQ-") ? `/marketing/sequences/${n}` : `/r/${n}`);
function inline(raw: string, key: string): ReactNode[] {
  // Models sometimes write markdown links; keep the link text (record numbers become app links below).
  const text = raw.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1").replace(/`([^`]+)`/g, "$1");
  return text.split(/(\*\*[^*]+\*\*|\b(?:OPP|ACC|ISS|CAPA|LEAD|CMP|EML|SEQ)-\d{5}\b|\bQ-\d{5}-[A-Z]\b)/g).map((part, i) => {
    if (!part) return null;
    if (/^\*\*.+\*\*$/.test(part)) return <b key={key + i}>{part.slice(2, -2)}</b>;
    if (/^(?:(?:OPP|ACC|ISS|CAPA|LEAD|CMP|EML|SEQ)-\d{5}|Q-\d{5}-[A-Z])$/.test(part)) return <Link key={key + i} to={hrefFor(part)}>{part}</Link>;
    return <Fragment key={key + i}>{part}</Fragment>;
  });
}
function Rich({ text }: { text: string }) {
  const lines = text.split("\n");
  const out: ReactNode[] = [];
  let list: ReactNode[] = [];
  const flush = () => { if (list.length) { out.push(<ul key={`ul${out.length}`}>{list}</ul>); list = []; } };
  lines.forEach((l, i) => {
    const m = l.match(/^\s*(?:[-*•]|\d+\.)\s+(.*)$/);
    if (m) list.push(<li key={i}>{inline(m[1], `l${i}`)}</li>);
    else { flush(); if (l.trim()) out.push(<p key={i}>{inline(l.replace(/^#+\s*/, ""), `p${i}`)}</p>); }
  });
  flush();
  return <>{out}</>;
}

export function Chat({ area, placeholder, compact, onChanged, context, prompts: own, beforeConfirm }: {
  area?: string; placeholder?: string; compact?: boolean; onChanged?: () => void;
  context?: string; // what's open, e.g. "process:<id>"
  prompts?: string[]; // starting suggestions in place of the area's
  beforeConfirm?: () => Promise<void>; // e.g. save the editor first, so a confirmed change lands on the latest version
}) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }); }, [turns.length, busy]);

  const send = async (q: string) => {
    const msg = q.trim();
    if (!msg || busy) return;
    const next: Turn[] = [...turns, { role: "user", content: msg }];
    setTurns(next); setText(""); setBusy(true); setError(null);
    try {
      const r = await api.chat(next.map((t) => ({ role: t.role, content: t.content })), area, context);
      setTurns([...next, { role: "assistant", content: r.reply, actions: r.actions }]);
    } catch (e) {
      // Stub until Courage Chat is switched on (no model key is set, so nothing is sent to a model).
      if (e instanceof ApiError && e.body?.code === "not_configured") {
        setTurns([...next, { role: "assistant", content: "Courage Chat is coming soon. When it's switched on, it will answer from your courageMFG records and Business Central, as you, and propose updates for you to confirm.\nFor now, find the record or page you need with the search at the top." }]);
      } else setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };
  const act = async (ti: number, a: ChatAction, confirm: boolean) => {
    const upd = (patch: Partial<NonNullable<Turn["actions"]>[number]>) => setTurns((ts) => ts.map((t, i) => (i !== ti ? t : { ...t, actions: t.actions?.map((x) => (x.id === a.id ? { ...x, ...patch } : x)) })));
    if (!confirm) return upd({ status: "dismissed" });
    try { await beforeConfirm?.(); await api.confirm(a); upd({ status: "done" }); onChanged?.(); } catch (e) { upd({ status: "failed", error: e instanceof Error ? e.message : String(e) }); }
  };
  const submit = (e: FormEvent) => { e.preventDefault(); send(text); };
  const prompts = own ?? ((area && PROMPTS[area]) || DEFAULT);

  return (
    <section className={`chat ${compact ? "compact" : ""}`} aria-label="Courage Chat">
      {turns.length > 0 && (
        <div className="chat-log" aria-live="polite">
          {turns.map((t, i) => t.role === "user" ? <div key={i} className="chat-me">{t.content}</div> : (
            <div key={i} className="chat-ai">
              <Rich text={t.content} />
              {t.actions?.map((a) => (
                <div key={a.id} className={`chat-action ${a.status ?? ""}`}>
                  <span className="lbl">{a.label}</span>
                  {!a.status && <span className="btns"><button className="btn primary" onClick={() => act(i, a, true)}>Confirm</button><button className="btn" onClick={() => act(i, a, false)}>Dismiss</button></span>}
                  {a.status === "done" && <span className="ok">Done{a.record ? <> · <Link to={hrefFor(a.record)}>Open {a.record}</Link></> : null}</span>}
                  {a.status === "dismissed" && <span className="muted">Dismissed</span>}
                  {a.status === "failed" && <span className="bad">Couldn't do it: {a.error}</span>}
                </div>
              ))}
            </div>
          ))}
          {busy && <div className="chat-ai thinking"><span className="spin" aria-hidden="true" />Looking that up…</div>}
          <div ref={end} />
        </div>
      )}
      {error && <div className="notify warn" role="alert"><span>{error}</span></div>}
      <form className="chat-input" onSubmit={submit}>
        <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1l1.6 4.4L14 7l-4.4 1.6L8 13l-1.6-4.4L2 7l4.4-1.6z" fill="currentColor" /></svg>
        <input aria-label="Ask a question or ask for an update" placeholder={placeholder ?? (compact ? "Ask a question" : "Ask a question or ask for an update")} value={text} onChange={(e) => setText(e.target.value)} disabled={busy} />
        <button className="btn primary" disabled={busy || !text.trim()}>Ask</button>
      </form>
      {turns.length === 0 && (
        <div className="chat-prompts">
          {prompts.map((p) => <button key={p} className="chat-prompt" onClick={() => send(p)}>{p}</button>)}
        </div>
      )}
      {turns.length > 0 && <div className="chat-foot"><span className="muted">Courage Chat answers from courageMFG and Business Central, as you. Updates wait for your Confirm.</span><button className="linkish" onClick={() => { setTurns([]); setError(null); }}>New chat</button></div>}
    </section>
  );
}
